import { format } from 'date-fns'

import { useDateStore } from '@/components/DateInput'
import { createInstance } from '@/helpers/api/base'
import { useQueryClient } from '@tanstack/react-query'
import { useAppMutation } from '@/helpers/api/mutation'
import { formatCreateLogDate } from '../helpers/formatCreateLogDate'
import { InputFormValues } from '../types'
import { useWorkstationStore } from '@/modules/Workstation/store'
import { useGetUserInfo, useNightShiftInfo } from '@/modules/Auth'
import { useNotification } from '@/ui/Notification'
import { useCombinedLogsTableFormStateStore } from '../store'
import { useGetReference } from './useGetReference'
import {
    ICreateControllerLogRequest,
    ICreateControllerLogResponse,
    IRtnTripAsString,
    Log,
    RouteSelectionMode,
} from '@/types/logs'
import { useAuthStore } from '@/store/auth-store'
import { useGarageStore } from '@/modules/FindGarage'
import { Role } from '@/types/auth'
import { isValidTimeFormat } from '@/helpers/isValidTimeFormat'
import { formatLogRequestBody } from '../helpers/formatLogRequestBody'
import { useGetCodes } from './useGetCodes'
import { useGetControllerLogParams } from '../apiHelpers/useGetControllerLogParams'
import { getCodeTypeId } from '../helpers/getCodeTypeId'

export const useCreateControllerLog = () => {
    const queryClient = useQueryClient()
    const { date } = useDateStore()
    const { routes, workstation } = useWorkstationStore()
    const { garage } = useGarageStore()
    const { data: userInfo } = useGetUserInfo()
    const { showNotification } = useNotification()
    const {
        selectedTrips,
        selectedOriginalTrips,
        report,
        locationType,
        mechanicalInfo,
        isBulkCurtailment,
        nightShiftDate,
    } = useCombinedLogsTableFormStateStore()
    const { data: reference, discardReference } = useGetReference()
    const { role } = useAuthStore()
    const logParams = useGetControllerLogParams()
    const { data: codes = [] } = useGetCodes()
    const { isNightShift, isAfterMidnight } = useNightShiftInfo()

    const createControllerLogMutation = useAppMutation<
        ICreateControllerLogResponse,
        Error,
        ICreateControllerLogRequest,
        Log[]
    >({
        mutationFn: (data) =>
            createInstance({
                url: '/controller-logs',
                method: 'POST',
                data,
            }),
        alertOnError: true,
        queryKey: ['controllerLogs', ...logParams],
        optimisticChangesCallback: (requestBody: ICreateControllerLogRequest, previousLogs: Log[] = []) => {
            const newLog = formatLogRequestBody({ requestBody, codes, userInfo })
            const logTimestampDate = new Date(newLog.timestamp)
            const currentDate = new Date(date)
            const addedNewLog = [...previousLogs, newLog]
            if (
                logTimestampDate.getDate() === currentDate.getDate() &&
                logTimestampDate.getMonth() === currentDate.getMonth() &&
                logTimestampDate.getFullYear() === currentDate.getFullYear()
            ) {
                discardReference()
                return addedNewLog
            }

            if (isNightShift && isAfterMidnight()) {
                const tomorrow = new Date(new Date().setDate(currentDate.getDate() + 1))
                if (
                    logTimestampDate.getDate() === tomorrow.getDate() &&
                    logTimestampDate.getMonth() === tomorrow.getMonth() &&
                    logTimestampDate.getFullYear() === tomorrow.getFullYear()
                ) {
                    discardReference()
                    return addedNewLog
                }
            }

            discardReference()
            return [...previousLogs]
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['unfinishedLogs'] })
            showNotification({ message: 'Record created' })
        },
    })

    const handleCreateControllerLog = (data: InputFormValues) => {
        const isWorkstationSelected =
            role === Role.MileageClerk ? !!garage?.routes.length : !!workstation?.workstationNumber

        const selectedRoutes = role === Role.MileageClerk ? garage?.routes : routes.map((route) => route.route)

        const selectedTimeStamp = isValidTimeFormat(data.timeStamp) ? data.timeStamp : format(new Date(), 'HH:mm')

        const timeStamp = formatCreateLogDate(nightShiftDate ?? date, selectedTimeStamp)

        const performCreate = () => {
            createControllerLogMutation.mutate({
                id: crypto.randomUUID(),
                reference: reference,
                code: data.code ? data.code : undefined,
                codeTypeId: getCodeTypeId(data.code),
                routes: !isWorkstationSelected
                    ? undefined
                    : data.routes && data.routes !== 'N/A'
                      ? [data.routes]
                      : selectedRoutes,
                routeSelectionMode: data.routes
                    ? data.routes === 'N/A'
                        ? RouteSelectionMode.All
                        : RouteSelectionMode.Default
                    : RouteSelectionMode.None,
                trip: data.trip,
                rtnTrips:
                    !!selectedTrips.length && !isBulkCurtailment
                        ? selectedTrips.map(
                              (trip) => ({ trip: String(trip.trip), order: trip.order }) as IRtnTripAsString
                          )
                        : data.rtnTrips
                          ? (String(data.rtnTrips)
                                .split(',')
                                .map((trip) => ({ trip: trip, order: 0 })) as IRtnTripAsString[])
                          : undefined,
                garageCode: data.depotCode ? data.depotCode : undefined,
                run: data.run ? data.run : undefined,
                duty: data.duty ? data.duty.split('/') : undefined,
                fleet: data.fleet ? data.fleet.split('/') : undefined,
                locationFrom: data.locationFrom,
                locationTo: data.locationTo,
                locationType: locationType ?? undefined,
                description: data.description ? data.description : undefined,
                extraInfo: data.extraInfo ? data.extraInfo : undefined,
                strategy: data.strategy ? data.strategy : undefined,
                delay: data.delay ? data.delay : undefined,
                mileage: data.mileage ? +data.mileage : undefined,
                isZeroMileage: data.isZeroMileage === 'true',
                timeStamp,
                author: userInfo?.lastSessionDetails.authorName ?? undefined,
                workstation: workstation?.workstationNumber,
                report,
                mechanicalInfo,
                originalTrip:
                    data.code === 'E'
                        ? {
                              routes: data.originalRoutes
                                  ? data.originalRoutes === 'N/A'
                                      ? selectedRoutes
                                      : [data.originalRoutes]
                                  : undefined,
                              trip: data.originalTrip,
                              rtnTrips:
                                  selectedOriginalTrips.length > 0
                                      ? selectedOriginalTrips.map(
                                            (trip) =>
                                                ({
                                                    trip: String(trip.trip),
                                                    order: trip.order,
                                                }) as IRtnTripAsString
                                        )
                                      : data.originalRtnTrips
                                        ? data.originalRtnTrips
                                              .split(',')
                                              .map((trip) => ({ trip: trip, order: 0 }) as IRtnTripAsString)
                                        : undefined,
                              run: data.originalRun ? data.originalRun : undefined,
                              duty: data.originalDuty ? data.originalDuty.split('/') : undefined,
                              fleet: data.originalFleet ? data.originalFleet.split('/') : undefined,
                          }
                        : undefined,
                eventLogId: data.eventLogId,
                selectedCodeTypeReasonId: data.selectedCodeTypeReasonId,
                direction: data.direction,
                bulkCurtailment:
                    data.bulkCurtailmentTripTo || isBulkCurtailment
                        ? {
                              tripTo: data.bulkCurtailmentTripTo?.toString() || '',
                              runTo: data.bulkCurtailmentRunTo?.toString() || '',
                              dutyTo: data.bulkCurtailmentDutyTo?.toString() || '',
                              fleetTo: data.bulkCurtailmentFleetTo?.toString() || '',
                          }
                        : undefined,
            })
        }

        if (!isValidTimeFormat(data.timeStamp)) {
            showNotification({
                type: 'error',
                title: 'Time cannot be blank',
                message: `Your record ${reference} will be saved with the time ${selectedTimeStamp} on ${format(timeStamp, 'dd MMM yyyy')} because the time field cannot be left blank.`,
                manualDismiss: true,
                errorDismissTitle: 'Close',
                onDismiss: performCreate,
            })
        } else {
            performCreate()
        }
    }

    return { handleCreateControllerLog, isPending: createControllerLogMutation.isPending }
}
